import { FC, useEffect, useState } from 'react';
import { Routes, Route, useMatch, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import { Footer, Header, NavigationPage } from '@ui-modules';
import { useAuth } from '@ui-modules/auth';
import { processLogoutNonFederatedUser } from '@common/utils';
import { ICrumb } from '@ui-modules/types';
import { LocalStorageKeys, SessionStorageKeys } from '@common/constants';
import {
  GlobalProvider,
  LogbookFilterProvider,
  LogbookProvider as LogbookStateProvider,
  RepositoryProvider,
  useGlobalContext,
} from '@context';
import { CallbackPage, ErrorPage, NoAccessPage, SignInPage, NotFoundPage } from '@pages';
import i18n from '@common/translations/i18n';
import routes from './routes';
import pkg from '../package.json';

const queryClient = new QueryClient();

interface IRouterElement {
  route: ICrumb;
}

const RouterElement: FC<IRouterElement> = ({ route }) => {
  const match = useMatch(route.path);
  return (
    <NavigationPage
      allRoutes={routes}
      givenRoute={route}
      routePath={match?.pattern.path ?? ''}
      routeParams={match?.params}
      additional={<ToastContainer />}
    />
  );
};

function App() {
  const federatedUserToken = sessionStorage.getItem(LocalStorageKeys.X_USER_TOKEN);

  const navigate = useNavigate();
  const { axios, isAuthenticated, userName, logout: logoutSSO } = useAuth();
  const { lang, setLang } = useGlobalContext();

  const [selectedLang, setSelectedLang] = useState<string>(lang);

  const logoutHandler = () => {
    if (federatedUserToken) {
      return processLogoutNonFederatedUser();
    }

    logoutSSO?.();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (
      !sessionStorage.getItem(SessionStorageKeys.AGENCIES) &&
      !sessionStorage.getItem(SessionStorageKeys.FACILITY)
    ) {
      window.open(`${process.env.REACT_APP_HBH_DO_URL}`, '_self');
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('selectedLang', selectedLang);
    setLang(selectedLang);
    i18n.changeLanguage(selectedLang);
  }, [selectedLang, setLang]);

  return (
    <QueryClientProvider client={queryClient}>
      <RepositoryProvider axios={axios}>
        <GlobalProvider>
          <LogbookStateProvider>
            <LogbookFilterProvider>
              {isAuthenticated ? (
                <Header
                  activeLang={selectedLang}
                  languages={['en', 'fr']}
                  serviceName="Drivers"
                  username={userName ?? ''}
                  logout={logoutHandler}
                  onLangChange={setSelectedLang}
                  onLogoClick={() => {
                    window.open(`${process.env.REACT_APP_HBH_DO_URL}`, '_self');
                  }}
                />
              ) : null}
              <Routes>
                {isAuthenticated ? (
                  <>
                    {routes.map((route) => (
                      <Route
                        path={route.path}
                        key={route.path}
                        element={<RouterElement route={route} />}
                      />
                    ))}
                    <Route path="callback/*" element={<CallbackPage />} />
                  </>
                ) : (
                  <>
                    <Route index element={<SignInPage />} />
                    <Route path="callback/*" element={<CallbackPage />} />
                    <Route path="forbidden" element={<NoAccessPage />} />
                    <Route path="error" element={<ErrorPage />} />
                  </>
                )}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              {isAuthenticated ? <Footer releaseVersion={pkg.version} /> : null}
            </LogbookFilterProvider>
          </LogbookStateProvider>
        </GlobalProvider>
      </RepositoryProvider>
    </QueryClientProvider>
  );
}

export default App;
